import React, { useState, useEffect, useContext, useRef } from "react";
import * as Sentry from '@sentry/browser';
import Api from "../../Api";
import { withAuth } from "../../hoc/Auth";
import {Column, Container, Wrapper} from "../../hoc/Layout";
import { FormattedMessage } from "react-intl";
import MessageContext from "../../MessageContext";
import Loader from "../../Components/Utils/Loader";

import classes from "./styles.module.css";

var ACCESS_ANTIFRAUD_FILE = 32768;

var INTERNAL_PROVIDERS = ["tkb", "finstar", "manual", "nx"];
var EXTERNAL_PROVIDERS = ["scanfraud"];

/**
 * Объединяет результаты провайдеров строки в 2 группы: Внутренняя / Внешняя.
 */
function mergeRowProviders(providers) {
    if (!providers) return { internal: null, external: null };

    var statusPriority = { blocked: 4, error: 3, ok: 2, skipped: 1 };

    function mergeGroup(codes) {
        var bestStatus = null;
        var bestPriority = 0;
        var reason = "";
        var hasAny = false;

        for (var i = 0; i < codes.length; i++) {
            var p = providers[codes[i]];
            if (!p) continue;
            hasAny = true;
            var prio = statusPriority[p.status] || 0;
            if (prio > bestPriority) {
                bestPriority = prio;
                bestStatus = p.status;
            }
            if (p.status === "blocked" && p.reason) {
                reason = p.reason;
            }
        }

        if (!hasAny) return null;
        return { status: bestStatus || "skipped", reason: reason };
    }

    return {
        internal: mergeGroup(INTERNAL_PROVIDERS),
        external: mergeGroup(EXTERNAL_PROVIDERS),
    };
}

export default withAuth(function ({ token, accountAccessSettings }) {
    var msgCtx = useContext(MessageContext);
    var onMessage = msgCtx.onMessage;
    var fileInputRef = useRef(null);

    var _pu = useState(null);
    var projectUUID = _pu[0];
    var setProjectUUID = _pu[1];

    var _r = useState(null);
    var batchResult = _r[0];
    var setBatchResult = _r[1];

    var _l = useState(false);
    var isChecking = _l[0];
    var setIsChecking = _l[1];

    var _lp = useState(true);
    var loadingProjects = _lp[0];
    var setLoadingProjects = _lp[1];

    var _drag = useState(false);
    var isDragging = _drag[0];
    var setIsDragging = _drag[1];

    // Load projects and auto-select first with ACCESS_ANTIFRAUD_FILE permission
    useEffect(function () {
        var cancelled = false;
        new Api({ token }).getProjects().then(function (data) {
            if (cancelled) return;
            var list = Array.isArray(data) ? data : [];
            var settings = accountAccessSettings || [];

            var found = null;
            for (var i = 0; i < list.length; i++) {
                var p = list[i];
                for (var j = 0; j < settings.length; j++) {
                    if (settings[j].merchantUUID === p.uuid && (settings[j].privileges & ACCESS_ANTIFRAUD_FILE) > 0) {
                        found = p.uuid;
                        break;
                    }
                }
                if (found) break;
            }

            if (!found && list.length > 0) {
                found = list[0].uuid;
            }

            if (found) {
                setProjectUUID(found);
            }
        }).catch(function (err) {
            Sentry.captureException(err);
            if (!cancelled) onMessage({ type: "error", message: "Ошибка загрузки проектов" });
        }).finally(function () {
            if (!cancelled) setLoadingProjects(false);
        });
        return function () { cancelled = true; };
    }, [token, accountAccessSettings]);

    function handleFile(file) {
        if (!file) return;

        var ext = (file.name || "").split(".").pop().toLowerCase();
        if (ext !== "xlsx" && ext !== "csv" && ext !== "xls") {
            onMessage({ type: "error", message: "Поддерживаются только файлы XLSX, XLS и CSV" });
            return;
        }

        if (!projectUUID) {
            onMessage({ type: "error", message: "Нет доступных проектов" });
            return;
        }

        setIsChecking(true);
        setBatchResult(null);

        var formData = new FormData();
        formData.append("file", file);
        formData.append("merchantUuid", projectUUID);

        new Api({ token }).antifraudBatchCheck(formData).then(function (data) {
            setBatchResult(data);
        }).catch(function (e) {
            Sentry.captureException(e);
            var msg = (e.response && e.response.data && e.response.data.message) || e.message || "Ошибка проверки файла";
            onMessage({ type: "error", message: msg });
        }).finally(function () {
            setIsChecking(false);
        });
    }

    function onDrop(e) {
        e.preventDefault();
        setIsDragging(false);
        var files = e.dataTransfer && e.dataTransfer.files;
        if (files && files.length > 0) {
            handleFile(files[0]);
        }
    }

    function onDragOver(e) {
        e.preventDefault();
        setIsDragging(true);
    }

    function onDragLeave(e) {
        e.preventDefault();
        setIsDragging(false);
    }

    function onFileSelect(e) {
        var files = e.target.files;
        if (files && files.length > 0) {
            handleFile(files[0]);
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    }

    function clearResults() {
        setBatchResult(null);
    }

    function downloadCsv() {
        if (!batchResult || !batchResult.results) return;

        var headers = ["#", "Карта", "Телефон", "ИНН", "IP", "Статус", "Внутренняя", "Внешняя"];
        var rows = [headers.join(";")];

        batchResult.results.forEach(function (r) {
            var d = r.data || {};
            var merged = mergeRowProviders(r.providers);

            function formatGroup(g) {
                if (!g) return "-";
                if (g.status === "blocked") return "НАЙДЕНО" + (g.reason ? ": " + g.reason : "");
                if (g.status === "ok") return "Не найдено";
                return g.status;
            }

            var row = [
                r.row,
                d.card_pan || "",
                d.phone || "",
                d.inn || "",
                d.ip || "",
                r.blocked ? "BLOCKED" : "OK",
                formatGroup(merged.internal),
                formatGroup(merged.external),
            ];
            rows.push(row.join(";"));
        });

        var csvContent = "\uFEFF" + rows.join("\n");
        var blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        var url = URL.createObjectURL(blob);
        var link = document.createElement("a");
        link.href = url;
        link.download = "antifraud-results.csv";
        link.click();
        URL.revokeObjectURL(url);
    }

    if (loadingProjects) {
        return (
            <Wrapper>
                <Container className={classes.StackOnMobile}>
                    <Column width="780" className={classes.MainColumn}>
                        <div className={classes.LoaderBox}>
                            <Loader />
                        </div>
                    </Column>
                </Container>
            </Wrapper>
        );
    }

    return (
        <Wrapper>
            <Container className={classes.StackOnMobile}>
                <Column width="780" className={classes.MainColumn}>
                            <div className={classes.Card}>
                                <h4 className={classes.Title}>
                                    <FormattedMessage id="UserMenu.title.antifraudFileCheck" defaultMessage="Проверка файла" />
                                </h4>

                                {/* Upload zone */}
                                {!batchResult && !isChecking && (
                                    <div
                                        className={isDragging ? classes.DropZoneActive : classes.DropZone}
                                        onDrop={onDrop}
                                        onDragOver={onDragOver}
                                        onDragLeave={onDragLeave}
                                        onClick={function () { fileInputRef.current && fileInputRef.current.click(); }}
                                    >
                                        <div className={classes.DropIcon}>&#128196;</div>
                                        <div className={classes.DropText}>
                                            Перетащите файл XLSX/CSV сюда или нажмите для выбора
                                        </div>
                                        <div className={classes.DropHint}>
                                            Столбцы: card_pan, phone, inn, ip (минимум 1)
                                        </div>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept=".xlsx,.xls,.csv"
                                            onChange={onFileSelect}
                                            style={{ display: "none" }}
                                        />
                                    </div>
                                )}

                                {/* Loading */}
                                {isChecking && (
                                    <div className={classes.CheckingBox}>
                                        <Loader />
                                        <div className={classes.CheckingText}>Проверка файла...</div>
                                    </div>
                                )}

                                {/* Results table */}
                                {batchResult && batchResult.results && (
                                    <div className={classes.ResultsSection}>
                                        <div className={classes.TableWrapper}>
                                            <table className={classes.Table}>
                                                <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>Данные</th>
                                                    <th>Статус</th>
                                                    <th>Внутренняя</th>
                                                    <th>Внешняя</th>
                                                    <th>Время</th>
                                                </tr>
                                                </thead>
                                                <tbody>
                                                {batchResult.results.map(function (r, idx) {
                                                    var d = r.data || {};
                                                    var dataStr = [
                                                        d.card_pan, d.phone, d.inn, d.ip
                                                    ].filter(Boolean).join(", ") || "-";
                                                    var merged = mergeRowProviders(r.providers);

                                                    function renderGroup(g) {
                                                        if (!g) return <td>-</td>;
                                                        var cls = g.status === "blocked" ? classes.ProviderBlocked
                                                            : g.status === "ok" ? classes.ProviderOk
                                                                : classes.ProviderSkipped;
                                                        var label = g.status === "blocked" ? "Найдено"
                                                            : g.status === "ok" ? "Не найдено"
                                                                : g.status === "error" ? "Ошибка"
                                                                    : "—";
                                                        var hint = g.reason || g.status;
                                                        return <td className={cls} title={hint}>{label}</td>;
                                                    }

                                                    return (
                                                        <tr key={idx} className={r.blocked ? classes.RowBlocked : ""}>
                                                            <td>{r.row}</td>
                                                            <td className={classes.DataCell} title={dataStr}>{dataStr}</td>
                                                            <td>
                                                            <span className={r.blocked ? classes.StatusBadgeBlocked : classes.StatusBadgeOk}>
                                                                {r.blocked ? "Блок" : "OK"}
                                                            </span>
                                                            </td>
                                                            {renderGroup(merged.internal)}
                                                            {renderGroup(merged.external)}
                                                            <td>{(r.checkTimeMs / 1000).toFixed(2)} сек</td>
                                                        </tr>
                                                    );
                                                })}
                                                </tbody>
                                            </table>
                                        </div>

                                        <div className={classes.Actions}>
                                            <button className={classes.BtnPrimary} onClick={clearResults}>
                                                Новая проверка
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                </Column>

                <Column width="350" className={classes.SideColumn}>
                            {/* Summary */}
                            {batchResult && (
                                <div className={classes.SideCard}>
                                    <h6 className={classes.SideTitle}>Результат</h6>
                                    <div className={classes.SummaryGrid}>
                                        <div className={classes.SummaryItem}>
                                            <span className={classes.SummaryLabel}>Файл:</span>
                                            <span className={classes.SummaryValue}>{batchResult.name}</span>
                                        </div>
                                        <div className={classes.SummaryItem}>
                                            <span className={classes.SummaryLabel}>Всего:</span>
                                            <span className={classes.SummaryValue}>{batchResult.total}</span>
                                        </div>
                                        <div className={classes.SummaryItem}>
                                            <span className={classes.SummaryLabel}>OK:</span>
                                            <span className={classes.SummaryValueOk}>{batchResult.ok}</span>
                                        </div>
                                        <div className={classes.SummaryItem}>
                                            <span className={classes.SummaryLabel}>Заблокировано:</span>
                                            <span className={classes.SummaryValueBlocked}>{batchResult.blocked}</span>
                                        </div>
                                        <div className={classes.SummaryItem}>
                                            <span className={classes.SummaryLabel}>Время:</span>
                                            <span className={classes.SummaryValue}>{(batchResult.totalTimeMs / 1000).toFixed(2)} сек</span>
                                        </div>
                                    </div>

                                    <button className={classes.BtnDownload} onClick={downloadCsv}>
                                        Скачать результат (CSV)
                                    </button>
                                </div>
                            )}

                            {/* Help */}
                            {!batchResult && (
                                <div className={classes.SideCard}>
                                    <h6 className={classes.SideTitle}>Формат файла</h6>
                                    <div className={classes.HelpText}>
                                        <p>Загрузите файл XLSX или CSV со следующими столбцами (минимум один):</p>
                                        <ul>
                                            <li><strong>card_pan</strong> — номер карты (13-19 цифр)</li>
                                            <li><strong>phone</strong> — телефон (в любом формате)</li>
                                            <li><strong>inn</strong> — ИНН (10 или 12 цифр)</li>
                                            <li><strong>ip</strong> — IP адрес</li>
                                        </ul>
                                        <p>Первая строка — заголовки. Максимум 1000 строк.</p>
                                    </div>
                                </div>
                            )}
                </Column>
            </Container>
        </Wrapper>
    );
});



// WEBPACK FOOTER //
// ./src/PagesOfUser/UserAntiFraudCheck/index.js